import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Cloud, RefreshCw, CheckCircle2, AlertTriangle, Link2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const SHEET_NAMES = {
  dcr: "Daily Calls Report (DCR)", "large-deals": "Large Deals", "bd-leads": "BD Leads",
  "dealer-north": "Dealer — North", "dealer-west": "Dealer — West",
  "dealer-south": "Dealer — South", "dealer-exports": "Dealer — Exports",
  "market-sizing": "Market Sizing",
};

export function SharePointSyncSettings({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data: state, isLoading } = useQuery({
    queryKey: ["sharepoint-status"],
    queryFn: async () => (await api.get("/sales-mis/sharepoint/status")).data,
    enabled: open,
  });
  const [url, setUrl] = useState(null);
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const curUrl = url ?? state?.url ?? "";
  const curEnabled = enabled ?? state?.enabled ?? true;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["sharepoint-status"] });
    qc.invalidateQueries({ queryKey: ["edit-sheet"] });
    qc.invalidateQueries({ queryKey: ["edit-sheets"] });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/sales-mis/sharepoint/config", { url: curUrl.trim(), enabled: curEnabled });
      if (!curUrl.trim()) toast.success("SharePoint sync saved.");
      else if (curEnabled) toast.success("Synced from SharePoint — auto-sync every 10 minutes is ON.");
      else toast.success("Synced once from SharePoint. Auto-sync is OFF — sheets stay editable and won't refresh automatically.");
      if (data.sync && data.sync.analytics_ok === false) {
        toast.error(`Sheets synced, but analytics parsing failed: ${data.sync.analytics_error || "unknown error"}`, { duration: 12000 });
      }
      setUrl(null); setEnabled(null);
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/sales-mis/sharepoint/sync-now");
      const upd = (data.sync?.results || []).filter((r) => r.status === "updated" || r.status === "created").length;
      if (data.sync?.analytics_ok === false) {
        toast.error(`Sheets synced, but analytics parsing failed: ${data.sync.analytics_error || "unknown error"}`, { duration: 12000 });
      } else {
        toast.success(upd ? `Synced — ${upd} sheet(s) updated from SharePoint.` : "Synced — everything already up to date.");
      }
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setSyncing(false); }
  };

  const results = state?.last_results || [];
  const unmatched = state?.unmatched_tabs || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="sharepoint-sync-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Cloud className="h-5 w-5 text-sky-600" /> SharePoint Live Sync</DialogTitle>
          <DialogDescription className="text-xs text-slate-400">
            The CRM pulls the MIS workbook from SharePoint every {state?.interval_minutes || 10} minutes. Synced sheets become read-only here — SharePoint is the source of truth.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-500 flex items-center gap-1.5 mb-1.5"><Link2 className="h-3.5 w-3.5" /> Share link ("Anyone with the link")</label>
              <Input value={curUrl} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://…sharepoint.com/:x:/g/personal/…"
                className="text-xs font-mono" data-testid="sharepoint-url-input" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
              <div>
                <p className="text-sm font-medium text-slate-800">Auto-sync every {state?.interval_minutes || 10} minutes</p>
                <p className="text-xs text-slate-400">Turning this off makes the sheets editable again.</p>
              </div>
              <Switch checked={curEnabled} onCheckedChange={setEnabled} data-testid="sharepoint-enabled-switch" />
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand/90 text-white gap-1.5" data-testid="sharepoint-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {saving ? "Saving & syncing…" : "Save & Sync"}
              </Button>
              {state?.enabled && state?.url && (
                <Button variant="outline" onClick={syncNow} disabled={syncing} className="gap-1.5" data-testid="sharepoint-sync-now-btn">
                  {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {syncing ? "Syncing…" : "Sync now"}
                </Button>
              )}
            </div>

            {state?.last_run_at && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 space-y-2" data-testid="sharepoint-last-status">
                <p className="text-xs flex items-center gap-1.5">
                  {state.last_status === "ok"
                    ? <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /><span className="text-slate-600">Last synced <b>{new Date(state.last_sync_at).toLocaleString()}</b></span></>
                    : <><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /><span className="text-amber-700">Last attempt failed: {state.last_error}{state.last_sync_at ? ` — showing data from ${new Date(state.last_sync_at).toLocaleString()}` : ""}</span></>}
                </p>
                {results.length > 0 && (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                    {results.map((r) => (
                      <p key={r.slug} className="text-[11px] text-slate-500 truncate" data-testid={`sharepoint-tab-${r.slug}`}>
                        <span className="font-medium text-slate-700">{SHEET_NAMES[r.slug] || r.slug}</span> ← “{r.tab.trim()}” · {r.rows} rows
                      </p>
                    ))}
                  </div>
                )}
                {unmatched.length > 0 && (
                  <p className="text-[11px] text-slate-400">Not synced (no matching CRM sheet): {unmatched.map((t) => t.trim()).join(", ")}</p>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
